import flatten from 'lodash/flatten'
import type { Dispatch } from 'redux'
import type { GroupId } from 'types/group'
import type { Contact, Recipient } from 'types/list'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { Thunk } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import Intercom from '@disco/api/intercom'
import api from '@disco/api/server'
import {
  CONTACTS_UPDATE_FAIL,
  CONTACTS_UPDATE_SUCCESS,
  PLAYLIST_SHARE,
  PLAYLIST_SHARE_ERROR,
  PLAYLIST_SHARE_SUCCESS,
  PLAYLIST_SHARES_LOADING,
  PLAYLIST_SHARES_LOADING_ERROR,
  PLAYLIST_SHARES_LOADING_SUCCESS,
} from '@disco/constants/ActionTypes'
import { EMAIL, RECIPIENT_GROUP } from '@disco/constants/RecipientTypes'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads sharing information for a specific playlist from the server.
 * @param playlistId - The ID of the playlist to load shares for
 * @returns Thunk that fetches and dispatches playlist share data
 */
export function getPlaylistShares(playlistId: PlaylistId): Thunk {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      type: PLAYLIST_SHARES_LOADING,
      id: playlistId,
    })

    try {
      const responseShares = await api.getPlaylistShares(playlistId)
      dispatch({
        type: PLAYLIST_SHARES_LOADING_SUCCESS,
        id: playlistId,
        responseShares,
      })
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: PLAYLIST_SHARES_LOADING_ERROR,
        id: playlistId,
        error,
        errorMessage: parseServerError(error.body),
      })
    }
  }
}
/**
 * Shares a playlist with specified recipients via email or generates a share URL.
 * @param playlist - The playlist to share
 * @param recipients - Array of recipients to share with
 * @param options - Sharing configuration options
 * @param options.manual - Whether this is a manual share (generates URL) or email share
 * @param options.message - Optional custom message to include
 * @param options.subject - Optional custom email subject
 * @param options.template - Optional email template to use
 * @param options.territory_ids - Optional territory restriction IDs
 * @param options.noPlaylistShareNotification - Whether to suppress success notifications
 * @returns Thunk that shares the playlist and handles analytics tracking
 */
export function playlistShare(
  playlist: Playlist,
  recipients: Array<Recipient>,
  {
    message,
    subject,
    manual,
    template,
    territory_ids,
    // TODO: replace playlistShare with bulk and drop the flag
    noPlaylistShareNotification,
  }: {
    manual: boolean
    message?: string
    subject?: string
    template?: string
    territory_ids?: GroupId[]
    noPlaylistShareNotification?: boolean
  },
): Thunk {
  return async dispatch => {
    dispatch({
      type: PLAYLIST_SHARE,
      playlist: playlist,
      recipients: recipients,
    })
    return api
      .emailPlaylist({
        id: playlist.playlist_id,
        manual: manual,
        subject: subject,
        message: message,
        template: template,
        territory_ids,
        recipients: recipients.map(recipient => {
          let result: Recipient | null = null

          if (recipient.type === EMAIL) {
            result = {
              email: recipient.email,
              type: recipient.type,
              name: recipient.name,
            }
          } else {
            result = {
              id: recipient.id,
              type: recipient.type,
              business_ids: recipient.business_ids
                ? recipient.business_ids
                : null,
            }
          }

          return result
        }),
      })
      .then(shareResult => {
        // we need to set recipients to original params as backend returns
        // recipients without businesses param, which is required if you want
        // to create email group later, add them manually
        const businesses = flatten(recipients.map(r => r.businesses)).filter(
          Boolean,
        )
        const normalizedShareResult = {
          ...shareResult,
          recipients: shareResult.recipients.map(recipient => {
            return {
              ...recipient,
              businesses:
                recipient.businesses ||
                (recipient.business_ids || [])
                  .map(id => businesses.find(bu => bu?.id === id))
                  .filter(Boolean),
            }
          }),
        }
        const duplicate =
          playlist.shares &&
          playlist.shares.items &&
          playlist.shares.items.includes(shareResult.id)
        dispatch({
          type: PLAYLIST_SHARE_SUCCESS,
          playlist,
          duplicate,
          result: normalizedShareResult,
        })

        if (!noPlaylistShareNotification) {
          if (manual) {
            if (!duplicate) {
              pushUINotification({
                message: 'Generated share URL!',
                level: 'success',
              })
            }
          } else {
            pushUINotification({
              message: 'Shared playlist!',
              level: 'success',
            })
          }
        }

        const hasEmailGroups = recipients.some(r => r.type === RECIPIENT_GROUP)

        if (hasEmailGroups) {
          dispatch(sendMixpanelEvent('Share playlist via email group'))
        }

        if (message && message.length) {
          dispatch(sendMixpanelEvent('Add share message'))
        }

        if (manual) {
          Intercom.sendRaw('Assign share')
        } else {
          Intercom.sendRaw('Email share')
          dispatch(
            sendMixpanelEvent('Email share', {
              Receivers: [{
                team_inboxes: recipients.filter(r => r.type === 'business')
                  .length,
                email_addresses: recipients.filter(r => r.type !== 'business')
                  .length,
              }],
              Type: 'Playlist',
            }),
          )
        }

        return { ...normalizedShareResult, duplicate }
      })
      .catch(error => {
        dispatch({
          type: PLAYLIST_SHARE_ERROR,
          playlist: playlist,
          error: error,
        })
        throw error
      })
  }
}
/**
 * Updates a specific field of a contact and syncs with the server.
 * @param contact - The contact to update
 * @param field - The field name to update
 * @param newValue - The new value for the field
 * @returns Thunk that updates the contact and dispatches success/failure actions
 */
export function editContactField(
  contact: Contact,
  field: string,
  newValue: string,
): Thunk {
  return async dispatch => {
    const updatedContact = { ...contact, [field]: newValue }

    try {
      await api.updateContacts([updatedContact])
      dispatch({
        type: CONTACTS_UPDATE_SUCCESS,
        contacts: [updatedContact],
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_UPDATE_FAIL,
        contacts: [updatedContact],
        error,
      })
      // Throws errors, callers need to wrap call with `.catch()`
      throw error
    }
  }
}
